const escapeHtml = value =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const money = value => Number(value || 0).toFixed(2)

const date = value => {
  if (!value) return ''
  return new Date(value).toLocaleString()
}

/**
 * Combine sold lines with the same product, unit, and unit price across invoices.
 *
 * @param {Array<Object>} invoices Close-shift invoices with item lines.
 * @returns {Array<Object>} Grouped lines in first-sale order.
 */
export const groupShiftReportItems = invoices => {
  const groups = new Map()

  ;(invoices || []).forEach(invoice => {
    ;(invoice.items || []).forEach(item => {
      const product = item.product_id == null
        ? [item.name_en, item.name_kh]
        : item.product_id
      const unit = item.uom_id == null
        ? [item.uom_en, item.uom_kh]
        : item.uom_id
      const price = Number(item.unit_price || 0).toFixed(2)
      const key = JSON.stringify([product, unit, price])
      const existing = groups.get(key)

      if (existing) {
        existing.quantity += Number(item.quantity || 0)
        existing.grand_total += Number(item.grand_total || 0)
      } else {
        groups.set(key, {
          ...item,
          quantity: Number(item.quantity || 0),
          grand_total: Number(item.grand_total || 0)
        })
      }
    })
  })

  return Array.from(groups.values())
}

/**
 * Build the detailed 80mm cashier close-shift report.
 *
 * @param {Object} data Shift summary and translated labels.
 * @returns {string} Printable report HTML.
 */
export const buildRetailSaleShiftReportTemplate = ({
  summary = {},
  labels = {},
  cashier = '',
  counter = '',
  company = '',
  branch = '',
  locale = 'en'
} = {}) => {
  const sales = summary.sales || {}
  const invoices = sales.invoices || []
  const itemMarkup = groupShiftReportItems(invoices)
    .map(item => {
      const name =
        locale === 'kh'
          ? item.name_kh || item.name_en || ''
          : item.name_en || item.name_kh || ''
      const uom =
        locale === 'kh'
          ? item.uom_kh || item.uom_en || ''
          : item.uom_en || item.uom_kh || ''
      return `<div class="item-row"><span class="item-name">${escapeHtml(
        name
      )}${
        uom ? ` (${escapeHtml(uom)})` : ''
      }</span><span class="item-qty">${escapeHtml(
        item.quantity
      )}</span><span class="item-price">${money(
        item.unit_price
      )}</span><strong>${money(item.grand_total)}</strong></div>`
    })
    .join('')

  const paymentMarkup = (summary.payments && summary.payments.methods
    ? summary.payments.methods
    : []
  )
    .map(
      payment =>
        `<div class="summary-row"><span>${escapeHtml(
          payment.payment_method
        )} (${escapeHtml(payment.currency_code)})</span><strong>${money(
          payment.input_amount
        )}</strong></div>`
    )
    .join('')

  return `<!doctype html><html lang="${
    locale === 'kh' ? 'km' : 'en'
  }"><head><meta charset="utf-8"><title>${escapeHtml(
    labels.title
  )}</title><style>@page{size:80mm auto;margin:0}*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff}body{width:420px;color:#18181b;font:600 12px/1.3 Arial,'DejaVu Sans',sans-serif}.report{width:100%;padding:22px 20px 26px;overflow:visible}.header{text-align:center}.title{margin:0;font-size:16px;font-weight:900;letter-spacing:.04em}.meta{margin:3px 0;color:#52525b;font-size:10px}.meta-list{margin-top:10px;padding-top:9px;border-top:1px dashed #a1a1aa;text-align:left;font-size:10.5px}.flex-row{display:flex;justify-content:space-between;gap:10px;padding:3px 0}.flex-row strong{font-weight:800;white-space:nowrap}.section{margin-top:12px;padding-top:9px;border-top:1px solid #18181b}.section-heading{display:flex;justify-content:space-between;gap:8px;padding-bottom:6px;border-bottom:1px dashed #d4d4d8;font-size:10.5px;font-weight:900;letter-spacing:.02em;text-transform:uppercase}.summary-row,.payment{display:flex;justify-content:space-between;gap:10px;padding:3px 0}.item-head,.item-row{display:grid;grid-template-columns:minmax(0,1fr) 38px 58px 60px;gap:5px;align-items:center}.item-head{min-height:21px;padding:4px 0;border-bottom:1px solid #d4d4d8;color:#52525b;font-size:9px;line-height:13px;text-transform:uppercase}.item-row{min-height:21px;padding:2px 0;font-size:10.5px;line-height:17px;overflow:visible}.item-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700}.item-qty{text-align:center;color:#52525b}.item-price,.item-row strong,.item-head span:nth-child(n+2){text-align:right;white-space:nowrap}.payment{padding:2px 0 0 10px;font-size:10px;color:#52525b}.grand-total{margin-top:7px;padding:8px;background:#f4f4f5;font-size:13px;font-weight:900}.grand-total strong{font-size:17px}.footer{text-align:center;color:#52525b;font-size:9px;margin-top:14px}@media print{body{width:80mm}.report{padding:6mm 5mm 8mm;-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body><main class="report"><header class="header"><p class="title">${escapeHtml(
    company
  )}</p><p class="meta">${escapeHtml(branch)}</p><p class="title report-title">${escapeHtml(
    labels.title
  )}</p><div class="meta-list"><div class="flex-row"><span>${escapeHtml(
    labels.dateTime || labels.period
  )}</span><strong>${escapeHtml(
    date(summary.closed_at)
  )}</strong></div><div class="flex-row"><span>${escapeHtml(
    labels.cashier
  )}</span><strong>${escapeHtml(
    cashier
  )}</strong></div><div class="flex-row"><span>${escapeHtml(
    labels.counter
  )}</span><strong>${escapeHtml(
    counter
  )}</strong></div></div></header><section class="section"><div class="section-heading"><span>${escapeHtml(
    labels.orderedItemsDetail || labels.invoiceDetails
  )}</span><span>${escapeHtml(invoices.length)} ${escapeHtml(
    labels.transactions || labels.items
  )}</span></div><div class="item-head"><span>${escapeHtml(
    labels.item
  )}</span><span>${escapeHtml(labels.qty)}</span><span>${escapeHtml(
    labels.price
  )}</span><span>${escapeHtml(labels.total)}</span></div>${itemMarkup ||
    `<p class="footer">${escapeHtml(
      labels.noInvoices
    )}</p>`}</section><section class="section"><div class="section-heading"><span>${escapeHtml(
    labels.summary
  )}</span><span>${escapeHtml(
    labels.currency || ''
  )}</span></div><div class="summary-row"><span>${escapeHtml(
    labels.grossSales
  )}</span><strong>${money(
    sales.sub_total
  )}</strong></div><div class="summary-row"><span>${escapeHtml(
    labels.discounts
  )}</span><strong>-${money(
    sales.discount
  )}</strong></div><div class="summary-row net-sales"><span>${escapeHtml(
    labels.netSales || labels.total
  )}</span><strong>${money(
    Number(sales.sub_total || 0) - Number(sales.discount || 0)
  )}</strong></div><div class="summary-row"><span>${escapeHtml(
    labels.tax
  )}</span><strong>+${money(
    sales.vat_amount
  )}</strong></div><div class="summary-row grand-total"><span>${escapeHtml(
    labels.total
  )}</span><strong>${money(
    sales.grand_total
  )}</strong></div><div class="summary-row completed-invoices"><span>${escapeHtml(
    labels.completedInvoices || labels.invoiceDetails
  )}</span><strong>${escapeHtml(invoices.length)} ${escapeHtml(
    labels.transactions || labels.items
  )}</strong></div><div class="payment"><strong>${escapeHtml(
    labels.paymentBreakdown
  )}</strong></div>${paymentMarkup}</section><footer class="footer">${escapeHtml(
    labels.closed
  )}</footer></main></body></html>`.replace(
    '</head>',
    '<style>.report,.report *{color:#000!important;-webkit-text-fill-color:#000!important}</style></head>'
  )
}

export default buildRetailSaleShiftReportTemplate
